// Lib dependencies
import { FC, useEffect, useState } from 'react';

// Components
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/UI/Select';
import { Label } from '@/components/UI/Label';

// Other
import { cn } from '@/lib/utils';

type Option = {
  value: string;
  label: string;
  onSelect?: () => void;
};

type OptionGroup = {
  value: string;
  label: string;
  options: Option[];
  onSelect?: () => void;
};

type SelectFieldProps = {
  label: string;
  options?: Option[];
  groups?: OptionGroup[];
  onChange: (value: string) => void;
  defaultValue?: string;
  error?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  placeholder: string;
  className?: string;
  hideErrorMessage?: boolean;
  bordered?: boolean;
};

const SelectField: FC<SelectFieldProps> = ({
  label,
  options,
  groups,
  onChange,
  defaultValue,
  error,
  disabled = false,
  icon,
  placeholder,
  className = '',
  hideErrorMessage,
  bordered = false,
}) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    defaultValue
  );

  useEffect(() => {
    setSelectedValue(defaultValue);
  }, [defaultValue]);

  const handleValueChange = (value: string) => {
    if (!disabled) {
      const selectedOption = options?.find((opt) => opt.value === value);
      const selectedGroup = groups?.find((group) => group.value === value);
      const groupOption = groups
        ?.flatMap((g) => g.options)
        .find((opt) => opt.value === value);

      setSelectedValue(value);
      onChange(value);

      if (selectedOption?.onSelect) selectedOption.onSelect();
      if (selectedGroup?.onSelect) selectedGroup.onSelect();
      if (groupOption?.onSelect) groupOption.onSelect();
    }
  };

  return (
    <div>
      <Label className="text-bg-blue font-semibold text-background-1 flex items-center">
        {icon && <div className="mr-2 mb-1">{icon}</div>}
        {label}
      </Label>
      <Select
        value={selectedValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            `w-full h-[53px] bg-white rounded-t-sm focus-visible:outline-none shadow-none transition duration-200 ease-in-out ${
              error ? 'border-b-2 border-destructive' : ''
            } ${
              disabled
                ? 'bg-white opacity-50 cursor-not-allowed'
                : 'hover:bg-gray-100'
            } ${bordered ? 'border border-gray-200' : 'border-none'}`,
            className
          )}
          disabled={disabled}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {groups
            ? groups.map((group) => (
                <div key={group.value} className="pt-2">
                  <SelectItem
                    value={group.value}
                    disabled={group.options.length > 0}
                    className={cn(
                      'font-bold text-black',
                      group.options.length > 0 && 'cursor-default'
                    )}
                  >
                    {group.label}
                  </SelectItem>
                  {group.options.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={disabled}
                      className="px-3"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </div>
              ))
            : options?.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={disabled}
                >
                  {option.label}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      {!hideErrorMessage && error && (
        <p className="text-destructive">{error}</p>
      )}
    </div>
  );
};

export default SelectField;
